import { createJob, setCreateJobStatus } from "actions/jobs";
import RequireData from "hooks/RequireData";
import SageButton from "kit/SageButton";
import SageErrorAlert from "kit/SageErrorAlert";
import React from "react";
import { Collapse, Form } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import createJobStatusSelector from "selectors/createjobstatus";
import memberTeamsSelector from "selectors/memberTeams";
import memberTeamsStatusSelector from "selectors/memberTeamsStatus";

interface Props {
    fileInfo: sage.model.IFileInfo;
}

const CreateJobForm: React.FC<Props> = ({ fileInfo }) => {
    const dispatch = useDispatch();

    const [name, setName] = React.useState("");
    const [renderEngine, setRenderEngine] = React.useState(
        fileInfo.render_engine
    );
    const [renderAnimation, setRenderAnimation] = React.useState(true);
    const [startFrame, setStartFrame] = React.useState(
        fileInfo.frame_start.toString()
    );
    const [endFrame, setEndFrame] = React.useState(
        fileInfo.frame_end.toString()
    );
    const [jobType, setJobType] = React.useState<sage.model.JobType>("PUBLIC");
    const [teamId, setTeamId] = React.useState("none");
    const [errorText, setErrorText] = React.useState<string | null>(null);
    const createJobStatus = useSelector(createJobStatusSelector);

    const onNameChange = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value),
        [setName]
    );
    const onRadioChange = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setRenderAnimation(e.target.value === "animation");
        },
        [setRenderAnimation]
    );
    const onRenderEngineChanged = React.useCallback(
        (e: React.ChangeEvent<HTMLSelectElement>) => {
            setRenderEngine(e.target.value);
        },
        [setRenderEngine]
    );
    const onStartFrameChanged = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) =>
            setStartFrame(e.target.value),
        [setStartFrame]
    );
    const onEndFrameChanged = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => setEndFrame(e.target.value),
        [setEndFrame]
    );
    const onJobTypeChanged = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) =>
            setJobType(e.target.value as sage.model.JobType),
        [setJobType]
    );
    const onTeamIdChanged = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => setTeamId(e.target.value),
        [setTeamId]
    );
    const [memberTeams] = RequireData(
        memberTeamsSelector,
        memberTeamsStatusSelector,
        {
            type: "com.sage.actions.team.IFetchMemberTeams",
        } as sage.actions.team.IFetchMemberTeams
    );

    const onSubmit = React.useCallback(
        (e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();

            const startFrameNum = parseInt(startFrame);
            const endFrameNum = parseInt(endFrame);

            if (isNaN(startFrameNum)) {
                setErrorText("Start frame must be a valid number");
                return;
            }

            if (renderAnimation && isNaN(endFrameNum)) {
                setErrorText("End frame must be a valid number");
                return;
            }

            if (renderAnimation && startFrameNum > endFrameNum) {
                setErrorText("End frame must be greater than start frame.");
                return;
            }

            if (startFrameNum < 0) {
                setErrorText("The start frame must be greater than 0");
                return;
            }

            dispatch(
                createJob(
                    fileInfo.file_id,
                    name,
                    renderEngine,
                    startFrameNum,
                    renderAnimation ? endFrameNum : startFrameNum,
                    renderAnimation,
                    jobType,
                    teamId === "none" ? null : teamId
                )
            );
        },
        [
            startFrame,
            endFrame,
            dispatch,
            fileInfo.file_id,
            name,
            renderEngine,
            renderAnimation,
            jobType,
            teamId,
        ]
    );

    const onDismiss = React.useCallback(() => {
        setErrorText(null);
    }, [setErrorText]);

    React.useEffect(() => {
        if (createJobStatus === "error") {
            setErrorText(
                "There was an error creating your job. Please try again."
            );
        }
    }, [createJobStatus]);

    React.useEffect(() => {
        dispatch(setCreateJobStatus("none"));
    }, [dispatch]);

    const shouldShowTeamSelector = React.useMemo(() => {
        return !!(
            jobType === "PRIVATE" &&
            memberTeams &&
            memberTeams.length > 0
        );
    }, [jobType, memberTeams]);

    return (
        <form onSubmit={onSubmit} id="create-job-form">
            <div className="form-group">
                <label htmlFor="nameInput">Job Name</label>
                <input
                    autoComplete="text"
                    value={name}
                    onChange={onNameChange}
                    type="name"
                    className="form-control"
                    id="nameInput"
                />
            </div>
            <div className="form-group">
                <label htmlFor="jobType">Job Type</label>
                <Form.Control
                    as="select"
                    onChange={onJobTypeChanged}
                    value={jobType}
                >
                    <option value="PUBLIC">Public</option>
                    <option value="PRIVATE">Private</option>
                </Form.Control>
                <Form.Text>
                    Public jobs will be rendered by everyone and cost you
                    minutes. Private jobs will be rendered by only your account
                    and an optional selected team and cost nothing.
                </Form.Text>
            </div>
            <Collapse in={shouldShowTeamSelector}>
                <div className="form-group">
                    <label htmlFor="teamSelector">Team</label>
                    <Form.Control
                        as="select"
                        onChange={onTeamIdChanged}
                        value={teamId}
                    >
                        <option value="none">No Team</option>
                        {(memberTeams || []).map((mt) => (
                            <option value={mt.id} key={mt.id}>
                                {mt.name}
                            </option>
                        ))}
                    </Form.Control>
                    <Form.Text>
                        Select a team to render this private job. Only members
                        of the team will be able to render it. Set as &quot;No
                        Team&quot; if you wnat only your account to render this
                        job.
                    </Form.Text>
                </div>
            </Collapse>
            <div className="form-group">
                <label htmlFor="animationOrImage">Animation or Image</label>
                <div className="form-check">
                    <input
                        className="form-check-input"
                        type="radio"
                        name="animationRadio"
                        id="animationRadio"
                        value="animation"
                        checked={renderAnimation}
                        onChange={onRadioChange}
                    />
                    <label
                        className="form-check-label"
                        htmlFor="animationRadio"
                    >
                        Render Animation
                    </label>
                </div>
                <div className="form-check">
                    <input
                        className="form-check-input"
                        type="radio"
                        name="imageRadio"
                        id="imageRadio"
                        value="image"
                        checked={!renderAnimation}
                        onChange={onRadioChange}
                    />
                    <label className="form-check-label" htmlFor="imageRadio">
                        Render Image
                    </label>
                </div>
            </div>
            <div className="form-group">
                <label htmlFor="renderEngineInput">Render Engine</label>
                <select
                    value={renderEngine}
                    className="custom-select"
                    onChange={onRenderEngineChanged}
                >
                    <option value="CYCLES">Cycles</option>
                    <option value="BLENDER_EEVEE">Eevee</option>
                    <option value="BLENDER_WORKBENCH">Blender Workbench</option>
                </select>
            </div>
            <div className="form-group">
                <div className="form-row">
                    <div className="col">
                        <label htmlFor="startFrame">Start Frame</label>
                        <input
                            onChange={onStartFrameChanged}
                            type="text"
                            value={startFrame}
                            className="form-control"
                            id="startFrame"
                        />
                    </div>
                    <div className="col">
                        <label htmlFor="endFrame">End Frame</label>
                        <input
                            onChange={onEndFrameChanged}
                            value={endFrame}
                            type="text"
                            className="form-control"
                            id="endFrame"
                            disabled={!renderAnimation}
                        />
                    </div>
                </div>
            </div>
            <SageButton
                loading={createJobStatus === "loading"}
                type="submit"
                text="Create Job"
            />
            <p>
                <small>Blender Version: {fileInfo.blender_version}</small>
            </p>
            {errorText && (
                <SageErrorAlert onDismiss={onDismiss} text={errorText} />
            )}
        </form>
    );
};

export default CreateJobForm;
